import { ReactNode, UIEvent } from 'react';

import './new-table.scss';

import { Preloader } from '~shared/ui/preloader/preloader';

export interface INewTableColumn<T> {
	key: string;
	title: ReactNode;
	width?: string;
	minWidth?: string;
	maxWidth?: string;
	align?: 'left' | 'center' | 'right';
	nowrap?: boolean;
	render?: (row: T, rowIndex: number) => ReactNode;
}

interface INewTableProps<T> {
	columns: INewTableColumn<T>[];
	rows: T[];
	pending?: boolean;
	emptyText?: string;
	className?: string;
	rowClassName?: (row: T, rowIndex: number) => string;
	onScroll?: (event: UIEvent<HTMLDivElement>) => void;
}

export function NewTable<T>({
	columns,
	rows,
	pending = false,
	emptyText = 'Записей не найдено',
	className,
	rowClassName,
	onScroll,
}: Readonly<INewTableProps<T>>) {
	return (
		<div className={`new-table ${className ?? ''}`.trim()}>
			<div className="new-table__scroll" onScroll={onScroll}>
				<table className="new-table__element">
					<thead>
						<tr>
							{columns.map(column => (
								<th
									key={column.key}
									style={{
										width: column.width,
										minWidth: column.minWidth,
										maxWidth: column.maxWidth,
										textAlign: column.align ?? 'left',
									}}
								>
									{column.title}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.length > 0 ? (
							rows.map((row, rowIndex) => (
								<tr key={`new-table-row-${rowIndex}`} className={rowClassName?.(row, rowIndex)}>
									{columns.map(column => (
										<td
											key={`${column.key}-${rowIndex}`}
											style={{
												textAlign: column.align ?? 'left',
												whiteSpace: column.nowrap ? 'nowrap' : 'normal',
											}}
										>
											{column.render
												? column.render(row, rowIndex)
												: (row as Record<string, ReactNode>)[column.key]}
										</td>
									))}
								</tr>
							))
						) : (
							<tr className="new-table__empty-row">
								<td colSpan={columns.length}>{emptyText}</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			{pending && (
				<div className="new-table__loader">
					<Preloader />
				</div>
			)}
		</div>
	);
}

export default NewTable;
